import { COMPANY, LAST_UPDATED, LEGAL_PATHS, VERIFICATION_URL, supportEmailLink } from './company';

// The ethy marketplace Terms of Service, i.e. the terms on which shoppers and
// sellers may use the platform itself. The terms of the *sale contract* between
// a shopper and a seller are a separate document — see
// termsAndConditionsOfSale.js — and this document links to it rather than
// repeating it.
const content = `
## 1. About these terms

These Terms of Service ("**Terms**") govern your use of the ${COMPANY.tradingName} marketplace at ${COMPANY.marketplaceUrl} (the "**Marketplace**") and any related pages, features
and services we provide (together, the "**Platform**").

The Platform is operated by ${COMPANY.legalName}, a company registered in ${COMPANY.jurisdiction} trading as ${COMPANY.tradingName} ("**${COMPANY.tradingName}**", "**we**", "**us**", "**our**").

By creating an account, browsing the Marketplace or placing an order, you agree to these Terms. If
you do not agree to them, please do not use the Platform.

Please also read our [Privacy Policy](${LEGAL_PATHS.privacyPolicy}), which explains how we handle your personal
data, and our [Terms & Conditions of Sale](${LEGAL_PATHS.termsAndConditionsOfSale}), which govern purchases
made through the Marketplace.

## 2. Definitions

- "**Shopper**" means a person who uses the Platform to browse or buy products.
- "**Seller**" means a brand or business that has been approved to list and sell products on the
  Marketplace.
- "**Listing**" means a product a Seller offers for sale on the Marketplace.
- "**Content**" means anything uploaded to or submitted through the Platform, including text,
  images, product information, sustainability evidence and messages.
- "**Ecolabel**" means one of the ${COMPANY.tradingName} ecolabels awarded to a brand or product following our
  verification process.

## 3. What ${COMPANY.tradingName} does

${COMPANY.tradingName} is a curated marketplace for brands whose sustainability claims we have verified. We
provide the venue: we verify the brands we list, we present their products, and we operate the
checkout and messaging that connect Shoppers and Sellers.

We are not the seller of the products listed on the Marketplace. Each sale contract is formed
directly between the Shopper and the Seller of the product. The Seller is responsible for the
product, its description, its packaging, its dispatch and its statutory guarantees. Our role in
that contract is limited to operating the Platform through which it is made and payment is taken.

Where we describe a brand or product as verified, that verification relates to the sustainability
criteria set out in our standards. It is not a warranty about the quality, fitness or safety of an
individual product, and it does not make us a party to the sale. You can read how brands and
products are verified at [How brands get verified](${VERIFICATION_URL}).

## 4. Eligibility and your account

To use the Platform you must be at least 18 years old and able to enter into a legally binding
contract. If you are using the Platform on behalf of a business, you confirm that you have
authority to bind that business to these Terms.

You are responsible for:

- providing accurate account and contact information, and keeping it up to date;
- keeping your password confidential and not sharing your account with anyone else; and
- everything that happens through your account.

Tell us straight away at ${supportEmailLink} if you believe someone has used your account without
your permission.

We may ask you to verify your email address before you can use certain features, and we may
refuse, suspend or close an account where we reasonably believe these Terms have been broken.

## 5. Acceptable use

When using the Platform you must not:

- break any applicable law, or use the Platform for any unlawful or fraudulent purpose;
- post Content that is false, misleading, defamatory, obscene, discriminatory or that infringes
  anyone else's rights;
- make sustainability, environmental or ethical claims that you cannot substantiate;
- impersonate anyone, or misrepresent your relationship with a brand or business;
- circumvent the Marketplace checkout in order to take a transaction off the platform and avoid fees;
- scrape, crawl, copy or harvest data from the Platform, or use it to build a competing product or
  dataset, except with our prior written consent;
- upload viruses or other malicious code, or otherwise interfere with the operation, security or
  integrity of the Platform; or
- attempt to gain unauthorised access to any part of the Platform, or to any account you do not
  own.

## 6. Buying through the Marketplace

Orders placed through the Marketplace are governed by our
[Terms & Conditions of Sale](${LEGAL_PATHS.termsAndConditionsOfSale}), which set out how a contract is
formed, how payment is taken, and your cancellation, return and refund rights.

Prices are shown in pounds sterling and include VAT where it applies. Delivery costs, dispatch
times and returns arrangements are set by each Seller and shown on the Listing or at checkout.

## 7. Selling on the Marketplace

Sellers are admitted to the Marketplace by application and remain subject to our verification
standards for as long as they sell through it. If you sell on the Marketplace, you agree that:

- you have the right to sell the products you list, and they comply with all applicable UK product
  safety, labelling, consumer and advertising law;
- every Listing is accurate and complete, including price, materials, ingredients, country of
  origin, delivery estimate and returns policy;
- any sustainability claim or Ecolabel shown against your products is supported by evidence you
  have given us and which remains true. You will tell us promptly if it stops being true;
- you will fulfil accepted orders within the dispatch time shown, and will honour Shoppers'
  statutory cancellation, return, repair, replacement and refund rights;
- you will respond to Shopper messages and complaints promptly and in good faith; and
- you will keep your payout details and business information current so that we can pay you.

Commission, subscription and payment processing fees are as agreed with you in writing when your
brand joins the Marketplace. Payments are collected and paid out through our payment provider (see
section 8). We may withhold or reverse a payout where an order is cancelled, refunded, charged back
or subject to an unresolved dispute.

If your verification lapses, or a Listing or claim turns out to be unsupported, we may remove the
Listing, pause your storefront or end your access to the Marketplace.

## 8. Payments

Payments on the Marketplace are processed by Stripe. By paying through the Marketplace you also
accept Stripe's terms, and Sellers receiving payouts accept the Stripe Connected Account Agreement.
We do not store your full card details.

## 9. Your Content

You keep ownership of the Content you submit. By submitting Content you grant us a worldwide,
nonexclusive, royalty free licence to host, store, reproduce, adapt (for formatting and
presentation) and display that Content for the purposes of operating, promoting and improving the
Platform. For Sellers, this licence includes using brand and product imagery in Marketplace
merchandising, newsletters and social channels, and it continues for a reasonable period after a
Listing is removed so that historic orders and pages remain intelligible.

You are responsible for your Content and confirm that you have the rights needed to submit it. We
may remove Content that we reasonably believe breaks these Terms or the law.

## 10. Our intellectual property

The Platform, the ${COMPANY.tradingName} name and logo, the Ecolabel artwork, our verification standards
and everything else we create remain our property or that of our licensors. Nothing in these Terms
transfers any of it to you. You may not use our brand assets or Ecolabels outside the Platform
without our written permission, and Sellers may use them only in the way, and for as long as, their
verification allows.

## 11. Availability of the Platform

We work to keep the Platform available and secure, but we do not guarantee that it will be
uninterrupted or free of errors. We may suspend, withdraw or change all or part of the Platform (for
maintenance, for security, or for business reasons) and will give notice where it is reasonable to
do so.

## 12. Suspension and termination

You may stop using the Platform at any time and ask us to close your account by emailing
${supportEmailLink}. Closing an account does not affect orders already placed, which remain
governed by the Terms & Conditions of Sale.

We may suspend or end your access to the Platform if you materially break these Terms, if we are
required to by law, or if we reasonably believe your use of the Platform poses a risk to other
users, to Sellers or to us. Where we can, we will tell you why and give you an opportunity to put
things right.

## 13. Our liability

Nothing in these Terms limits or excludes our liability for death or personal injury caused by our
negligence, for fraud or fraudulent misrepresentation, or for anything else that cannot lawfully be
limited or excluded. In particular, these Terms do not affect your statutory rights as a consumer.

Subject to that:

- we are not responsible for the products themselves, or for a Seller's performance of a sale
  contract, beyond the responsibilities we take on in the
  [Terms & Conditions of Sale](${LEGAL_PATHS.termsAndConditionsOfSale});
- we are not liable for loss or damage that was not foreseeable, meaning loss or damage that was
  neither an obvious consequence of our breach nor contemplated by you and us when you started
  using the Platform; and
- if you use the Platform for any commercial or business purpose, we will have no liability to you
  for any loss of profit, loss of business, business interruption or loss of business opportunity,
  and our total liability to you in connection with the Platform will not exceed the greater of the
  fees you paid us in the 12 months before the claim arose, or £100.

## 14. Indemnity (Sellers only)

If you are a Seller, you agree to indemnify us against any claim, loss, liability or reasonable
cost we incur as a result of your breach of these Terms, of a sale contract with a Shopper, or of
any applicable product, consumer or advertising law, including any claim that a product or
sustainability claim of yours was unsafe, unlawful or unsubstantiated.

## 15. Privacy

We process personal data in accordance with our [Privacy Policy](${LEGAL_PATHS.privacyPolicy}). Sellers who
receive Shopper personal data in order to fulfil an order are independent controllers of that data
and must handle it in line with UK data protection law, using it only to fulfil and support that
order.

## 16. Complaints and how to contact us

If something has gone wrong, please email ${supportEmailLink} and tell us your order number (if
you have one) and what has happened. We aim to acknowledge complaints within five working days.

If your complaint concerns a product or an order, contact the Seller first through the Marketplace;
if it is not resolved, we will help mediate between you.

## 17. Changes to these Terms

We may change these Terms to reflect changes in the law, in our services or in how the Marketplace
operates. Where a change materially affects you, we will give reasonable notice, for example by
email or by a notice on the Platform. The current version is always available on this page and the
"Last updated" date shows when it last changed. Continuing to use the Platform after a change takes
effect means you accept the updated Terms.

## 18. Other important terms

- **No partnership.** Nothing in these Terms makes you and us partners, agents or employees of one
  another.
- **Transfer.** We may transfer our rights and obligations under these Terms to another
  organisation, for example on a sale or reorganisation of our business. You may not transfer yours
  without our written consent.
- **Severability.** If any provision of these Terms is found to be unenforceable, the rest continue
  to apply.
- **No waiver.** If we delay in enforcing a provision, that does not prevent us from enforcing it
  later.
- **Third parties.** These Terms are between you and us. No one else has any right to enforce them.

## 19. Governing law

These Terms and any dispute arising out of them are governed by the laws of ${COMPANY.jurisdiction}
and are subject to the nonexclusive jurisdiction of the courts of ${COMPANY.jurisdiction}. If you
live in Scotland or Northern Ireland, you may also bring proceedings in your local courts.

## 20. Contact

- ${COMPANY.legalName}, trading as ${COMPANY.tradingName}
- Email: ${supportEmailLink}
- Website: [${COMPANY.website}](${COMPANY.website})
`;

const termsOfService = {
  sections: [
    {
      sectionType: 'article',
      sectionId: 'terms-of-service',
      appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
      title: { fieldType: 'heading1', content: 'Terms of Service' },
      description: { fieldType: 'paragraph', content: `Last updated: ${LAST_UPDATED}` },
      blocks: [
        {
          blockType: 'defaultBlock',
          blockId: 'terms-of-service-content',
          text: { fieldType: 'markdown', content },
        },
      ],
    },
  ],
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: `Terms of Service | ${COMPANY.tradingName}`,
    },
    pageDescription: {
      fieldType: 'metaDescription',
      content: `The terms on which shoppers and verified brands may use the ${COMPANY.tradingName} marketplace.`,
    },
  },
};

export default termsOfService;
